import { useQuery } from "@tanstack/react-query";
import Session from "supertokens-auth-react/recipe/session";
import { User } from "../types/auth";
import { handleApiError, getErrorMessage } from "../utils/errorHandler";
import { authService } from "../services/authService";

export const userKeys = {
  all: ['user'] as const,
  current: () => [...userKeys.all, 'current'] as const,
};

interface UseUserReturn {
  user: User | null;
  isLoading: boolean;
  error: string | null;
  fetchUser: () => void;
}

export const useUser = (): UseUserReturn => {
  const {
    data: user = null,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: userKeys.current(),
    queryFn: async () => {
      // Prefer the logged-in user when both a real session and an old
      // anonymous share token/user are present in storage.
      const sessionExists = await Session.doesSessionExist();

      if (sessionExists) {
        const sessionInfo = await Session.getAccessTokenPayloadSecurely();

        const userInfo: User = {
          id: sessionInfo.id,
          email: sessionInfo.email || "",
          emailVerified: sessionInfo.email_verified || false,
          timeJoined: sessionInfo.timeJoined || Date.now(),
        };

        return userInfo;
      }

      const anonymousUser = authService.getAnonymousUser();
      if (anonymousUser) {
        const userInfo: User = {
          id: anonymousUser.id,
          email: anonymousUser.email || "",
          emailVerified: anonymousUser.loginMethods?.[0]?.verified || false,
          timeJoined: anonymousUser.timeJoined || Date.now(),
        };
        return userInfo;
      }

      throw new Error("No active session");
    },
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    user,
    isLoading,
    error: error ? getErrorMessage(handleApiError(error)) : null,
    fetchUser: () => refetch(),
  };
};
